/* eslint-disable react/require-default-props */
import React, {
  forwardRef,
  Suspense,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import {
  CanvasDocument,
  CatalogoAtendimento,
  downloadCanvasDocx,
  fetchAtendimentoCatalogo,
  PecaSalvaPayload,
  saveCanvas,
  saveCanvasPeticao,
} from 'services/actions/dr_licita_chat_actions';

import * as PS from '../DrLicitaPeticoes/styles';
import * as C from './canvas_styles';
import { copiarFormatado, copiarTexto, imprimir, textoPuro } from './editor/copiar';
import { contarLacunas, contarNotasDeUso } from './editor/lacunas';
import type { PeticaoEditorHandle } from './editor/PeticaoEditor';
import remarkPeticao, { prepararParaLeitura } from './editor/remarkPeticao';

// Painel da petição do modo Atendimento (o "Canvas" do IAGov): o agente
// escreve a petição aqui ao vivo; quando o turno acaba, a folha vira um editor
// estilo Word (o markdown continua sendo o formato salvo), com salvamento
// automático, Word, PDF e Salvar em Petições.

// O editor visual é um pedaço à parte do bundle: carrega quando o painel abre
const carregarEditor = () =>
  import(/* webpackChunkName: "editor-peticao" */ './editor/PeticaoEditor');
const PeticaoEditor = React.lazy(carregarEditor);

export interface CanvasLive {
  title: string;
  content: string;
  streaming: boolean;
  updating: boolean;
}

export interface CanvasPanelHandle {
  // Salva a edição pendente antes de um novo turno (o agente lê o documento
  // salvo; sem isso a última digitação ficaria de fora)
  flush: () => Promise<void>;
}

interface Props {
  chatId: number;
  document: CanvasDocument | null;
  live: CanvasLive | null;
  // O agente está respondendo: edição bloqueada até o turno acabar
  busy: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  onDocumentChange: (document: CanvasDocument) => void;
  onNotice: (texto: string) => void;
  // "Pedir ao Dr. Licita" sobre o trecho selecionado (markdown do trecho)
  onAskAboutSelection?: (trecho: string) => void;
}

type SaveState = 'idle' | 'pendente' | 'salvando' | 'salvo' | 'erro';

// Quebra de linha simples vira quebra de verdade na folha: o modelo escreve
// endereçamento e fecho em linhas seguidas, que o markdown juntaria num
// parágrafo só. E a tag <br> que o modelo às vezes escreve vira quebra (em
// célula de tabela, " / "): o markdown daqui não interpreta HTML. Mesma regra
// do Word e de Petições (agente, peticao.com_quebras). Só na exibição: o texto
// salvo continua como o modelo escreveu.
const BR = /[ \t]*<br\s*\/?>[ \t]*/gi;

const semTagBr = (linhas: string[]): string[] => {
  const saida: string[] = [];
  let emCodigo = false;
  linhas.forEach((linha) => {
    if (linha.trimStart().startsWith('```')) {
      emCodigo = !emCodigo;
      saida.push(linha);
    } else if (!emCodigo && /<br/i.test(linha)) {
      if (linha.trimStart().startsWith('|')) saida.push(linha.replace(BR, ' / '));
      else saida.push(...linha.replace(BR, '\n').split('\n'));
    } else {
      saida.push(linha);
    }
  });
  return saida;
};

const comQuebras = (markdown: string): string => {
  const linhas = semTagBr(markdown.split('\n'));
  let emCodigo = false;
  return linhas
    .map((linha, indice) => {
      if (linha.trimStart().startsWith('```')) {
        emCodigo = !emCodigo;
        return linha;
      }
      const proxima = linhas[indice + 1] || '';
      const quebra =
        !emCodigo &&
        linha.trim() !== '' &&
        proxima.trim() !== '' &&
        !linha.trimStart().startsWith('|') &&
        !proxima.trimStart().startsWith('|') &&
        !proxima.trimStart().startsWith('```') &&
        !linha.endsWith('  ');
      return quebra ? `${linha.trimEnd()}  ` : linha;
    })
    .join('\n');
};

const AUTOSAVE_MS = 3000;
const PLUGINS_DA_FOLHA = [gfm, remarkPeticao];

// Se o pedaço do editor não carregar (rede, deploy no meio), o painel volta
// ao modo de antes: leitura e edição do markdown
class ReservaDoEditor extends React.Component<
  { onFalha: () => void; children: React.ReactNode },
  { falhou: boolean }
> {
  constructor(props: { onFalha: () => void; children: React.ReactNode }) {
    super(props);
    this.state = { falhou: false };
  }

  static getDerivedStateFromError() {
    return { falhou: true };
  }

  componentDidCatch() {
    const { onFalha } = this.props;
    onFalha();
  }

  render() {
    const { falhou } = this.state;
    const { children } = this.props;
    return falhou ? null : children;
  }
}

const CanvasPanel = forwardRef<CanvasPanelHandle, Props>(
  (
    {
      chatId,
      document,
      live,
      busy,
      expanded,
      onToggleExpand,
      onClose,
      onDocumentChange,
      onNotice,
      onAskAboutSelection,
    },
    ref,
  ) => {
    // Reserva (editor visual indisponível): a troca Editar/Visualizar de antes
    const [editando, setEditando] = useState(false);
    const [modoMarkdown, setModoMarkdown] = useState(false);
    const [editorIndisponivel, setEditorIndisponivel] = useState(false);
    const [texto, setTexto] = useState(document?.content || '');
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [conflito, setConflito] = useState<CanvasDocument | null>(null);
    const [peticao, setPeticao] = useState<PecaSalvaPayload | null>(null);
    const [salvandoPeticao, setSalvandoPeticao] = useState(false);
    const [escolha, setEscolha] = useState<{ tipo: string; lei: string } | null>(null);
    const [catalogo, setCatalogo] = useState<CatalogoAtendimento | null>(null);
    const [menuAberto, setMenuAberto] = useState(false);
    const [confirmarLacunas, setConfirmarLacunas] = useState<'word' | 'peticoes' | null>(null);
    const [seguir, setSeguir] = useState(true);

    // Versão que o painel viu e texto ainda não salvo: refs para o autosave
    // e o flush enxergarem o valor atual sem esperar um render
    const versaoRef = useRef(document?.version || 0);
    const pendenteRef = useRef<string | null>(null);
    const timerRef = useRef<number | null>(null);
    const emVooRef = useRef<Promise<void> | null>(null);
    const editorRef = useRef<PeticaoEditorHandle>(null);
    const folhaRef = useRef<HTMLDivElement>(null);
    const corpoRef = useRef<HTMLDivElement>(null);
    const lacunaDaFolhaRef = useRef(-1);

    // O pedaço do editor começa a baixar assim que o painel abre
    useEffect(() => {
      carregarEditor().catch(() => undefined);
    }, []);

    // Documento novo vindo do agente (ou da troca de conversa): o painel o
    // adota. Com digitação pendente, a mesma versão é a do próprio PUT (segue
    // digitando); versão nova é conflito, e o usuário decide
    useEffect(() => {
      if (!document) return;
      if (pendenteRef.current !== null) {
        if (document.version !== versaoRef.current) setConflito(document);
        return;
      }
      versaoRef.current = document.version;
      setTexto(document.content);
    }, [document]);

    useEffect(() => {
      setPeticao(null);
      setConflito(null);
      setEditando(false);
      setModoMarkdown(false);
      setConfirmarLacunas(null);
      pendenteRef.current = null;
    }, [chatId]);

    useEffect(() => {
      if (busy) {
        setEditando(false);
        setMenuAberto(false);
        setConfirmarLacunas(null);
      }
    }, [busy]);

    // Durante a escrita, o painel rola junto com o texto até o usuário rolar
    // para cima (aí aparece "Acompanhar a escrita")
    const escrevendo = Boolean(live?.streaming);
    const corpoAoVivo = live?.content;
    useEffect(() => {
      if (escrevendo) setSeguir(true);
    }, [escrevendo]);

    useEffect(() => {
      const corpo = corpoRef.current;
      if (escrevendo && seguir && corpo) corpo.scrollTop = corpo.scrollHeight;
    }, [corpoAoVivo, escrevendo, seguir]);

    const aoRolar = () => {
      const corpo = corpoRef.current;
      if (!escrevendo || !corpo) return;
      const distancia = corpo.scrollHeight - corpo.scrollTop - corpo.clientHeight;
      if (distancia > 48 && seguir) setSeguir(false);
      else if (distancia <= 8 && !seguir) setSeguir(true);
    };

    const salvarAgora = async (): Promise<void> => {
      // A digitação que espera a pausa do editor entra já
      editorRef.current?.descarregar();
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (emVooRef.current) await emVooRef.current;
      const conteudo = pendenteRef.current;
      if (conteudo === null) return;
      setSaveState('salvando');
      const tarefa = (async () => {
        const resultado = await saveCanvas(chatId, conteudo, versaoRef.current);
        if (resultado.ok) {
          versaoRef.current = resultado.document.version;
          // Só limpa o pendente se ninguém digitou durante o PUT
          if (pendenteRef.current === conteudo) pendenteRef.current = null;
          setSaveState(pendenteRef.current === null ? 'salvo' : 'pendente');
          onDocumentChange(resultado.document);
        } else if ('conflict' in resultado) {
          setConflito(resultado.conflict);
          setSaveState('erro');
        } else {
          setSaveState('erro');
        }
      })();
      emVooRef.current = tarefa;
      try {
        await tarefa;
      } finally {
        emVooRef.current = null;
      }
    };

    useImperativeHandle(ref, () => ({ flush: salvarAgora }));

    const editar = (valor: string) => {
      setTexto(valor);
      // Voltou ao que está salvo (desfez tudo): nada a gravar
      const igualAoSalvo =
        document && valor === document.content && versaoRef.current === document.version;
      if (igualAoSalvo && !emVooRef.current) {
        pendenteRef.current = null;
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        setSaveState('idle');
        return;
      }
      pendenteRef.current = valor;
      setSaveState('pendente');
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        salvarAgora();
      }, AUTOSAVE_MS);
    };

    // Conflito: o documento mudou em outro lugar depois que o painel o leu
    const usarVersaoAtual = () => {
      if (!conflito) return;
      pendenteRef.current = null;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      versaoRef.current = conflito.version;
      setTexto(conflito.content);
      onDocumentChange(conflito);
      setConflito(null);
      setSaveState('idle');
    };

    const manterMinha = async () => {
      if (!conflito) return;
      versaoRef.current = conflito.version;
      setConflito(null);
      await salvarAgora();
    };

    const titulo = live?.title || document?.title || 'Petição';
    const conteudoVisivel = live ? live.content : texto;
    const versao = document?.version;
    const lacunas = contarLacunas(conteudoVisivel);
    const notasDeUso = contarNotasDeUso(conteudoVisivel);

    // O HTML da folha à vista: o editor quando ele está aberto, senão a leitura
    const htmlDaFolha = (): string =>
      editorRef.current?.html() || folhaRef.current?.innerHTML || '';

    const avisoDeLacunas = lacunas
      ? ` Atenção: ainda há ${lacunas} ${lacunas === 1 ? 'lacuna' : 'lacunas'} a completar.`
      : '';

    const copiar = () => {
      setMenuAberto(false);
      const html = htmlDaFolha();
      if (html && copiarFormatado(html))
        onNotice(`Petição copiada com a formatação.${avisoDeLacunas}`);
      else onNotice('Não foi possível copiar. Selecione o texto e copie manualmente.');
    };

    const copiarComoTexto = async () => {
      setMenuAberto(false);
      const conteudo = textoPuro(htmlDaFolha());
      if (conteudo && (await copiarTexto(conteudo))) {
        onNotice(
          `Texto copiado (${conteudo.length.toLocaleString('pt-BR')} caracteres).${avisoDeLacunas}`,
        );
      } else {
        onNotice('Não foi possível copiar. Selecione o texto e copie manualmente.');
      }
    };

    const copiarMarkdown = async () => {
      setMenuAberto(false);
      const ok = await copiarTexto(live?.content || texto);
      onNotice(ok ? 'Markdown copiado.' : 'Não foi possível copiar.');
    };

    const imprimirPdf = async () => {
      setMenuAberto(false);
      await salvarAgora();
      if (!imprimir(htmlDaFolha(), titulo)) onNotice('Não foi possível abrir a impressão agora.');
    };

    // Próxima lacuna: no editor, seleciona (para digitar por cima); na
    // leitura, rola até ela e destaca
    const proximaLacuna = () => {
      if (editorRef.current?.proximaLacuna()) return;
      const folha = folhaRef.current;
      if (!folha) return;
      const marcas = Array.prototype.slice.call(
        folha.querySelectorAll('mark.lacuna, mark.nota-de-uso'),
      ) as HTMLElement[];
      if (!marcas.length) return;
      lacunaDaFolhaRef.current = (lacunaDaFolhaRef.current + 1) % marcas.length;
      const alvo = marcas[lacunaDaFolhaRef.current];
      alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      alvo.classList.add('lacuna-foco');
      window.setTimeout(() => alvo.classList.remove('lacuna-foco'), 1600);
    };

    const baixar = async () => {
      setConfirmarLacunas(null);
      await salvarAgora();
      const ok = await downloadCanvasDocx(chatId, document?.title || 'Peticao');
      if (!ok) onNotice('Não foi possível gerar o Word agora. Tente novamente.');
    };

    const salvarEmPeticoes = async (ids?: { document_type_id: number; law_type_id: number }) => {
      setConfirmarLacunas(null);
      await salvarAgora();
      setSalvandoPeticao(true);
      const resultado = await saveCanvasPeticao(chatId, ids);
      setSalvandoPeticao(false);
      if (resultado.ok) {
        setPeticao(resultado.peticao);
        setEscolha(null);
        return;
      }
      if ('precisa' in resultado) {
        // O painel não trouxe o tipo ou a lei: o usuário escolhe
        if (!catalogo) fetchAtendimentoCatalogo().then(setCatalogo);
        setEscolha({
          tipo: resultado.precisa.document_type_id
            ? String(resultado.precisa.document_type_id)
            : '',
          lei: resultado.precisa.law_type_id ? String(resultado.precisa.law_type_id) : '',
        });
        return;
      }
      onNotice(resultado.error);
    };

    // Lacuna em aberto não bloqueia: pede uma confirmação
    const comLacunas = (acao: 'word' | 'peticoes') => {
      if (lacunas > 0) {
        setConfirmarLacunas(acao);
        return;
      }
      if (acao === 'word') baixar();
      else salvarEmPeticoes();
    };

    let status: { texto: string; tom: 'neutro' | 'ativo' | 'ok' | 'alerta' } | null = null;
    if (live?.streaming) status = { texto: 'Escrevendo…', tom: 'ativo' };
    else if (live?.updating) status = { texto: 'Atualizando…', tom: 'ativo' };
    else if (saveState === 'salvando' || saveState === 'pendente')
      status = { texto: 'Salvando…', tom: 'ativo' };
    else if (saveState === 'erro') status = { texto: 'Não salvo', tom: 'alerta' };
    else if (saveState === 'salvo') status = { texto: 'Salvo', tom: 'ok' };
    else if (versao) status = { texto: `Versão ${versao}`, tom: 'neutro' };

    const semDocumento = !conteudoVisivel.trim();
    const bloqueado = busy || Boolean(live?.streaming || live?.updating);

    // O que o corpo mostra: leitura durante o turno; depois, o editor visual
    // (ou o markdown, no modo avançado e na reserva)
    let modo: 'leitura' | 'visual' | 'markdown' = 'leitura';
    if (!semDocumento && !bloqueado) {
      if (modoMarkdown) modo = 'markdown';
      else if (!editorIndisponivel) modo = 'visual';
      else if (editando) modo = 'markdown';
    }

    const folhaDeLeitura = (
      <C.Sheet ref={folhaRef}>
        <ReactMarkdown remarkPlugins={PLUGINS_DA_FOLHA}>
          {prepararParaLeitura(comQuebras(conteudoVisivel))}
        </ReactMarkdown>
      </C.Sheet>
    );

    const alternarMarkdown = () => {
      setMenuAberto(false);
      editorRef.current?.descarregar();
      setModoMarkdown(!modoMarkdown);
    };

    return (
      <C.Panel expanded={expanded} data-clarity-mask="True">
        <C.Header>
          <i className="material-icons" style={{ color: '#23405c' }} aria-hidden="true">
            description
          </i>
          <h3 title={titulo}>{titulo}</h3>
          {!semDocumento && lacunas > 0 && (
            <C.Chip
              type="button"
              tone="lacuna"
              onClick={proximaLacuna}
              title="Lacunas [inserir: …] a completar antes de protocolar. Clique para ir à próxima."
            >
              Lacunas: {lacunas}
              <i className="material-icons" aria-hidden="true">
                arrow_downward
              </i>
            </C.Chip>
          )}
          {status && (
            <C.Status tone={status.tom} aria-live="polite">
              {status.texto}
            </C.Status>
          )}
        </C.Header>
        <C.Toolbar>
          {editorIndisponivel && !modoMarkdown && (
            <C.ToolButton
              type="button"
              disabled={bloqueado || semDocumento}
              onClick={() =>
                editando ? salvarAgora().then(() => setEditando(false)) : setEditando(true)
              }
              title={editando ? 'Voltar à leitura' : 'Editar o texto da petição'}
            >
              <i className="material-icons" aria-hidden="true">
                {editando ? 'visibility' : 'edit'}
              </i>
              {editando ? 'Visualizar' : 'Editar'}
            </C.ToolButton>
          )}
          <C.ToolButton
            type="button"
            disabled={semDocumento}
            onClick={copiar}
            title="Copiar com a formatação (para colar no Word ou no e-mail)"
          >
            <i className="material-icons" aria-hidden="true">
              content_copy
            </i>
            Copiar
          </C.ToolButton>
          <C.ToolButton
            type="button"
            disabled={bloqueado || semDocumento}
            onClick={() => comLacunas('word')}
            title="Baixar em Word"
          >
            <i className="material-icons" aria-hidden="true">
              download
            </i>
            Word
          </C.ToolButton>
          <C.ToolButton
            type="button"
            primary
            disabled={bloqueado || semDocumento || salvandoPeticao}
            onClick={() => comLacunas('peticoes')}
            title="Salvar em Petições"
          >
            <i className="material-icons" aria-hidden="true">
              save
            </i>
            {salvandoPeticao ? 'Salvando…' : 'Salvar em Petições'}
          </C.ToolButton>
          <C.Spacer />
          <C.MenuWrapper>
            <C.ToolButton
              type="button"
              aria-label="Mais opções"
              aria-haspopup="menu"
              aria-expanded={menuAberto}
              title="Mais opções"
              disabled={semDocumento}
              onClick={() => setMenuAberto(!menuAberto)}
            >
              <i className="material-icons" aria-hidden="true">
                more_vert
              </i>
            </C.ToolButton>
            {menuAberto && (
              <C.Menu role="menu">
                <button type="button" role="menuitem" onClick={copiarComoTexto}>
                  <i className="material-icons" aria-hidden="true">
                    notes
                  </i>
                  Copiar como texto (portal)
                </button>
                <button type="button" role="menuitem" disabled={bloqueado} onClick={imprimirPdf}>
                  <i className="material-icons" aria-hidden="true">
                    print
                  </i>
                  Imprimir / salvar em PDF
                </button>
                <button type="button" role="menuitem" onClick={copiarMarkdown}>
                  <i className="material-icons" aria-hidden="true">
                    code
                  </i>
                  Copiar como markdown
                </button>
                {!editorIndisponivel && (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={bloqueado}
                    onClick={alternarMarkdown}
                  >
                    <i className="material-icons" aria-hidden="true">
                      {modoMarkdown ? 'edit' : 'code'}
                    </i>
                    {modoMarkdown ? 'Voltar ao editor visual' : 'Editar markdown (avançado)'}
                  </button>
                )}
              </C.Menu>
            )}
          </C.MenuWrapper>
          <C.ToolButton
            type="button"
            onClick={onToggleExpand}
            aria-label={expanded ? 'Restaurar o painel' : 'Expandir o painel'}
            title={expanded ? 'Restaurar' : 'Expandir'}
          >
            <i className="material-icons" aria-hidden="true">
              {expanded ? 'close_fullscreen' : 'open_in_full'}
            </i>
          </C.ToolButton>
          <C.ToolButton
            type="button"
            onClick={() => salvarAgora().then(onClose)}
            aria-label="Fechar o painel"
            title="Fechar o painel"
          >
            <i className="material-icons" aria-hidden="true">
              close
            </i>
          </C.ToolButton>
        </C.Toolbar>

        {conflito && (
          <C.Banner tone="alerta">
            A petição foi alterada em outro lugar enquanto você editava.
            <button type="button" onClick={usarVersaoAtual}>
              Usar a versão atual
            </button>
            <button type="button" onClick={manterMinha}>
              Manter a minha edição
            </button>
          </C.Banner>
        )}
        {confirmarLacunas && (
          <C.Banner tone="alerta">
            Ainda há {lacunas} {lacunas === 1 ? 'lacuna' : 'lacunas'} [inserir: …] na petição.
            <button
              type="button"
              onClick={() => {
                setConfirmarLacunas(null);
                proximaLacuna();
              }}
            >
              Ir para a próxima
            </button>
            <button
              type="button"
              onClick={() => (confirmarLacunas === 'word' ? baixar() : salvarEmPeticoes())}
            >
              {confirmarLacunas === 'word' ? 'Baixar assim mesmo' : 'Salvar assim mesmo'}
            </button>
            <button type="button" onClick={() => setConfirmarLacunas(null)}>
              Cancelar
            </button>
          </C.Banner>
        )}
        {!semDocumento && notasDeUso > 0 && (
          <C.Banner tone="alerta">
            A petição tem {notasDeUso === 1 ? 'uma nota de uso' : `${notasDeUso} notas de uso`} da
            tese ([nota de uso: …]): é orientação de uso e não deve ir para o documento final.
            <button type="button" onClick={proximaLacuna}>
              Ir para a nota
            </button>
          </C.Banner>
        )}
        {peticao && (
          <C.Banner tone="info">
            Petição {peticao.atualizada ? 'atualizada' : 'salva'} em Petições.
            <Link to={`/dr_licita/peticoes?document=${peticao.id}`}>Abrir em Petições</Link>
          </C.Banner>
        )}

        <C.Body ref={corpoRef} onScroll={aoRolar}>
          {semDocumento && (
            <C.Vazio>
              <i className="material-icons" aria-hidden="true">
                edit_note
              </i>
              {live?.streaming
                ? 'O Dr. Licita está começando a escrever a petição…'
                : 'Nenhuma petição neste painel ainda. Quando o Dr. Licita redigir a petição, ela aparece aqui, pronta para editar, baixar em Word ou salvar em Petições.'}
            </C.Vazio>
          )}
          {modo === 'visual' && (
            <ReservaDoEditor onFalha={() => setEditorIndisponivel(true)}>
              <Suspense fallback={folhaDeLeitura}>
                <PeticaoEditor
                  ref={editorRef}
                  markdown={texto}
                  onChange={editar}
                  onSalvarAgora={() => {
                    salvarAgora();
                  }}
                  onPedirAoDrLicita={onAskAboutSelection}
                />
              </Suspense>
            </ReservaDoEditor>
          )}
          {modo === 'markdown' && (
            <C.Editor
              value={texto}
              spellCheck
              onChange={(event) => editar(event.target.value)}
              aria-label="Texto da petição (markdown)"
            />
          )}
          {!semDocumento && modo === 'leitura' && folhaDeLeitura}
          {escrevendo && !seguir && (
            <C.Acompanhar type="button" onClick={() => setSeguir(true)}>
              <i className="material-icons" aria-hidden="true">
                arrow_downward
              </i>
              Acompanhar a escrita
            </C.Acompanhar>
          )}
        </C.Body>

        {escolha && (
          <Modal show centered onHide={() => setEscolha(null)}>
            <PS.ModalContainer>
              <div className="modal-header">
                <h5 className="modal-title text-nowrap">
                  <strong>Salvar em Petições</strong>
                </h5>
                <button
                  aria-label="Close"
                  className="close"
                  type="button"
                  onClick={() => setEscolha(null)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <PS.ModalBody>
                <label htmlFor="canvas-tipo-peticao">
                  Tipo de petição
                  <C.Select
                    id="canvas-tipo-peticao"
                    value={escolha.tipo}
                    onChange={(event) => setEscolha({ ...escolha, tipo: event.target.value })}
                  >
                    <option value="">Selecione</option>
                    {(catalogo?.tipos || []).map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nome.replace(/^[A-Za-z]\.\s*/, '')}
                      </option>
                    ))}
                  </C.Select>
                </label>
                <label htmlFor="canvas-lei-peticao">
                  Legislação
                  <C.Select
                    id="canvas-lei-peticao"
                    value={escolha.lei}
                    onChange={(event) => setEscolha({ ...escolha, lei: event.target.value })}
                  >
                    <option value="">Selecione</option>
                    {(catalogo?.leis || []).map((lei) => (
                      <option key={lei.id} value={lei.id}>
                        {lei.nome}
                      </option>
                    ))}
                  </C.Select>
                </label>
              </PS.ModalBody>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={!escolha.tipo || !escolha.lei || salvandoPeticao}
                  onClick={() =>
                    salvarEmPeticoes({
                      document_type_id: Number(escolha.tipo),
                      law_type_id: Number(escolha.lei),
                    })
                  }
                >
                  Salvar
                </button>
              </div>
            </PS.ModalContainer>
          </Modal>
        )}
      </C.Panel>
    );
  },
);

CanvasPanel.displayName = 'CanvasPanel';

export default CanvasPanel;
